import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState('');
  
  // CSV Upload states
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvUploadResults, setCsvUploadResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [showDetailedInstructions, setShowDetailedInstructions] = useState(false);
  
  // New Students feature state
  const [showAddNewStudents, setShowAddNewStudents] = useState(false);
  const [newStudentFile, setNewStudentFile] = useState(null);
  const [newStudentUploadLoading, setNewStudentUploadLoading] = useState(false);
  const [newStudentUploadResults, setNewStudentUploadResults] = useState(null);
  const [showNewStudentResults, setShowNewStudentResults] = useState(false);

  // Quick Verify feature state
  const [showQuickVerify, setShowQuickVerify] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [quickVerifyData, setQuickVerifyData] = useState({
    scholar_status: '',
    gpa: '',
    major: '',
    classification: ''
  });
  const [quickVerifyLoading, setQuickVerifyLoading] = useState(false);
  const [quickVerifySuccess, setQuickVerifySuccess] = useState(false);

  // Priority Students CTA Alert
  const [showPriorityAlert, setShowPriorityAlert] = useState(false);

  // Feature toggle state
  const [raceRequired, setRaceRequired] = useState(false);

  // Dropdown options for Quick Verify
  const scholarStatusOptions = ['Graduated', 'Continuing', 'Health Leave of Absence', 'Co-Op Leave of Absence', 'Temporary Leave Status', 'Disqualification Withdrawal', 'University Withdrawal', 'External Transfer', 'Permanent Leave'];
  const classificationOptions = ['College - 1st Year', 'College - 2nd Year', 'College - 3rd Year', 'College - 4th Year', 'College - 5th Year', 'N/A'];

  useEffect(() => {
    fetchDashboardData();
    fetchRaceRequiredSetting();
  }, []);

  // Fetch race required setting from admin settings
  const fetchRaceRequiredSetting = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/partner_verification_race_required/public`);
      setRaceRequired(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching race required setting:', err);
      // Default to false on error (data minimization)
      setRaceRequired(false);
    }
  };

  // Show priority alert when dashboard data loads with priority students
  useEffect(() => {
    if (dashboardData?.priority_students && dashboardData.priority_students.length > 0) {
      // Check if user has already seen alert this session
      const alertSeen = sessionStorage.getItem('priorityAlertSeen');
      if (!alertSeen) {
        setShowPriorityAlert(true);
        sessionStorage.setItem('priorityAlertSeen', 'true');
      }
    }
  }, [dashboardData]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('partnerToken');
    
    if (!token) {
      navigate('/partner/login');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/partner/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setDashboardData(response.data);
    } catch (err) {
      console.error('Dashboard error:', err);
      if (err.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partnerInfo');
        navigate('/partner/login');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerInfo');
    navigate('/partner/login');
  };

  const navigateToVerification = (url) => {
    // Open verification form in same tab
    window.location.href = url;
  };

  // CSV Upload Functions
  const downloadTemplate = async () => {
    const token = localStorage.getItem('partnerToken');
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partner/verification-template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `verification_template_${dashboardData?.partner.university?.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const uploadCSV = async () => {
    if (!selectedFile) {
      setError('Please select a CSV file first');
      return;
    }

    const token = localStorage.getItem('partnerToken');
    setCsvUploadLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/partner/upload-verification-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setCsvUploadResults(response.data);
      setShowUploadResults(true);
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) fileInput.value = '';
      
      // Refresh dashboard data to show updated counts
      fetchDashboardData();

    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError(err.response?.data?.detail || 'Failed to upload CSV. Please try again.');
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const closeUploadResults = () => {
    setShowUploadResults(false);
    setCsvUploadResults(null);
  };

  // New Students File Selection Handler
  const handleNewStudentFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setNewStudentFile(file);
      setError('');
    }
  };

  // New Students Template Download Function
  const downloadNewStudentTemplate = async () => {
    const token = localStorage.getItem('partnerToken');
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partner/new-students-template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `new_students_template_${dashboardData?.partner.university?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error downloading new student template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  // New Students CSV Upload Function
  const uploadNewStudentsCSV = async () => {
    if (!newStudentFile) {
      setError('Please select a CSV file first');
      return;
    }

    const token = localStorage.getItem('partnerToken');
    setNewStudentUploadLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', newStudentFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/partner/add-students-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Handle upload response
      console.log('Upload response:', response.data);
      setNewStudentUploadResults(response.data);
      setShowNewStudentResults(true);
      setNewStudentFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('new-student-csv-file-input');
      if (fileInput) fileInput.value = '';
      
      // Only refresh if there were successful uploads
      if (response.data.successful && response.data.successful.length > 0) {
        fetchDashboardData();
      }

    } catch (err) {
      console.error('Error uploading new students CSV:', err);
      setError(err.response?.data?.detail || 'Failed to upload CSV. Please try again.');
    } finally {
      setNewStudentUploadLoading(false);
    }
  };

  // Scroll to Priority Students section
  const scrollToPriorityStudents = () => {
    setShowPriorityAlert(false);
    const element = document.getElementById('priority-students-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Quick Verify Functions
  const openQuickVerify = (student) => {
    setSelectedStudent(student);
    setQuickVerifyData({
      scholar_status: student.applicant_status_verified_sp25 || '',
      gpa: student.gpa_verified || '',
      major: student.major_verified || student.major || '',  // Pre-populate with student's major
      classification: student.current_academic_class_verified || ''
    });
    setQuickVerifySuccess(false);
    setShowQuickVerify(true);
  };

  const closeQuickVerify = () => {
    setShowQuickVerify(false);
    setSelectedStudent(null);
    setQuickVerifyData({
      scholar_status: '',
      gpa: '',
      major: '',
      classification: ''
    });
    setQuickVerifySuccess(false);
  };

  const handleQuickVerifySubmit = async (e) => {
    e.preventDefault();
    setQuickVerifyLoading(true);
    setError('');

    const token = localStorage.getItem('partnerToken');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/partner/quick-verify`,
        {
          student_id: selectedStudent.id,
          scholar_status: quickVerifyData.scholar_status,
          gpa: quickVerifyData.gpa,
          major: quickVerifyData.major,
          classification: quickVerifyData.classification
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setQuickVerifySuccess(true);

      // Refresh dashboard data to show updated student
      setTimeout(() => {
        fetchDashboardData();
        closeQuickVerify();
      }, 1500);

    } catch (err) {
      console.error('Quick verify error:', err);
      setError(err.response?.data?.detail || 'Failed to quick verify student. Please try again.');
    } finally {
      setQuickVerifyLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-800">{error}</p>
          <button
            onClick={() => navigate('/partner/login')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Mobile Header Layout - 30% Larger */}
          <div className="md:hidden space-y-4 py-2">
            {/* Logo - 30% larger */}
            <div className="flex justify-center">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 w-auto"
              />
            </div>
            
            {/* Dashboard Title - 30% larger */}
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-900">Partner Dashboard</h1>
            </div>
            
            {/* University Name - 30% larger */}
            <div className="text-center px-2">
              <p className="text-base text-gray-600 truncate">
                {dashboardData?.partner.university}
              </p>
            </div>
            
            {/* Welcome Message - 30% larger */}
            <div className="text-center px-2">
              <p className="text-base text-gray-700 truncate">
                Welcome, {dashboardData?.partner.full_name}
              </p>
            </div>
            
            {/* Sign Out Button - 30% larger */}
            <div className="flex justify-center pt-2">
              <button
                onClick={handleLogout}
                className="px-4 py-2.5 text-base text-white bg-gray-600 hover:bg-gray-700 rounded-md font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>

          {/* Desktop Header Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 w-auto mr-4"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Partner Dashboard</h1>
                <p className="text-sm text-gray-600">{dashboardData?.partner.university}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Welcome, {dashboardData?.partner.full_name}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Students
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {dashboardData?.statistics.total_students || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Verified
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {dashboardData?.statistics.verified_students || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pending
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {dashboardData?.statistics.pending_students || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Completion Rate
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {dashboardData?.statistics.verification_rate || 0}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Navigation</h3>
          <div className="flex flex-wrap gap-3">
            {dashboardData?.priority_students && dashboardData.priority_students.length > 0 && (
              <button
                onClick={() => scrollToSection('priority-students-section')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Priority Students ({dashboardData.priority_students.length})
              </button>
            )}

            {dashboardData?.has_active_assignments && (
              <button
                onClick={() => scrollToSection('verification-assignments-section')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Verification Assignments
              </button>
            )}

            <button
              onClick={() => scrollToSection('bulk-upload-section')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Upload
            </button>

            {dashboardData?.partner?.is_primary_verifier && (
              <button
                onClick={() => scrollToSection('add-students-section')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Add New Students
              </button>
            )}

            <button
              onClick={() => scrollToSection('recent-activity-section')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Recent Activity
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Instructions Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            Verify students via form by clicking into Active Verification Assignments OR read the instructions carefully and choose Bulk Verification Upload to verify multiple students with one spreadsheet upload.
          </p>
        </div>

        {/* Active Verification Assignments */}
        <div id="verification-assignments-section" className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Active Verification Assignments</h2>
            {dashboardData?.has_active_assignments && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {dashboardData.active_assignments.length} Active
              </span>
            )}
          </div>

          {dashboardData?.active_assignments && dashboardData.active_assignments.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.active_assignments.map((assignment, index) => (
                <div
                  key={assignment.token}
                  className={`border rounded-lg p-4 ${
                    assignment.is_expired ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          Verification Task #{index + 1}
                        </span>
                        {assignment.days_until_expiry <= 3 && !assignment.is_expired && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Expires in {assignment.days_until_expiry} day{assignment.days_until_expiry !== 1 ? 's' : ''}
                          </span>
                        )}
                        {assignment.is_expired && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Expired
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Created:</span> {new Date(assignment.created_at).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">Expires:</span> {new Date(assignment.expires_at).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">Students:</span> {assignment.total_students}
                        </div>
                        <div>
                          <span className="font-medium">Progress:</span> {assignment.verified_students}/{assignment.total_students}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${assignment.total_students > 0 
                              ? (assignment.verified_students / assignment.total_students * 100) 
                              : 0}%` 
                          }}
                        ></div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {assignment.remaining_students > 0
                            ? `${assignment.remaining_students} student${assignment.remaining_students !== 1 ? 's' : ''} remaining`
                            : '✓ All students verified'}
                        </span>
                        {!assignment.is_expired && assignment.remaining_students > 0 && (
                          <button
                            onClick={() => navigateToVerification(assignment.verification_url)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Open Verification Form
                            <svg className="ml-2 -mr-0.5 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                No active verification assignments
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Your administrator will assign verification tasks when needed
              </p>
            </div>
          )}
        </div>

        {/* CSV Upload Section - Only for Primary Verifiers */}
        {dashboardData?.partner && (
          <div id="bulk-upload-section" className="bg-white shadow rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Bulk Verification Upload (Update records for existing NACME students)</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Upload student verification data using CSV format
                </p>
              </div>
              {/* Show primary verifier badge if applicable */}
              {dashboardData.partner.is_primary_verifier && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Primary Verifier
                </span>
              )}
            </div>

            <div className="space-y-4">
              {/* Step 1: Download Template */}
              <div className="border-l-4 border-blue-400 pl-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Step 1: Download Template</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Download a CSV template with your assigned students pre-filled
                </p>
                <button
                  onClick={downloadTemplate}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download CSV Template
                </button>
              </div>

              {/* Step 2: Upload CSV */}
              <div className="border-l-4 border-green-400 pl-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Step 2: Upload Completed CSV</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Fill out the template and upload it here for bulk verification
                </p>
                
                {/* Mobile Layout - Stacked vertically */}
                <div className="md:hidden space-y-4">
                  <div>
                    <input
                      id="csv-file-input-mobile"
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  <button
                    onClick={uploadCSV}
                    disabled={!selectedFile || csvUploadLoading}
                    className={`w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white ${
                      !selectedFile || csvUploadLoading
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                    }`}
                  >
                    {csvUploadLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload CSV
                      </>
                    )}
                  </button>
                </div>

                {/* Desktop Layout - Side by side */}
                <div className="hidden md:flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      id="csv-file-input-desktop"
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  <button
                    onClick={uploadCSV}
                    disabled={!selectedFile || csvUploadLoading}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                      !selectedFile || csvUploadLoading
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                    }`}
                  >
                    {csvUploadLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload CSV
                      </>
                    )}
                  </button>
                </div>

                {selectedFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>

              {/* Requirements */}
              <div className="bg-gray-50 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">CSV Requirements:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use the downloaded template for correct formatting</li>
                  <li>• All fields are required except "conferred_grad_date"</li>
                  <li>• Dates must be in YYYY-MM-DD format</li>
                  <li>• GPA should be in 0.00 format (0.0 to 4.0)</li>
                  <li>• Use exact values for dropdown fields (Y/N, status options, etc.)</li>
                </ul>
                
                {/* Progressive Disclosure for Detailed Instructions */}
                <div className="text-center mt-3">
                  <button
                    onClick={() => setShowDetailedInstructions(!showDetailedInstructions)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    {showDetailedInstructions ? 'Hide' : 'Show'} detailed instructions
                  </button>
                </div>

                {showDetailedInstructions && (
                  <div className="mt-4 p-4 bg-white rounded-md border-l-4 border-blue-400">
                    <h5 className="font-medium text-gray-900 mb-3">Detailed CSV Upload Guide</h5>
                    
                    <div className="space-y-4 text-sm text-gray-700">
                      <div>
                        <h6 className="font-medium mb-1">Before You Start:</h6>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Ensure you have verification data ready for your students</li>
                          <li>Have access to Excel, Google Sheets, or similar spreadsheet software</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h6 className="font-medium mb-1">Field Specifications:</h6>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li><strong>Dates:</strong> Must be YYYY-MM-DD format (e.g., 2025-01-15)</li>
                          <li><strong>GPA:</strong> Decimal format 0.00-4.00 (e.g., 3.75)</li>
                          <li><strong>Status fields:</strong> Use exact values: Y, N</li>
                          <li><strong>Student ID:</strong> Never modify - used for matching records</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h6 className="font-medium mb-1">Common Issues to Avoid:</h6>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Don't change column headers or order</li>
                          <li>Don't add extra columns or rows</li>
                          <li>Don't use text like "Yes/No" instead of "Y/N"</li>
                          <li>Don't leave required fields empty</li>
                        </ul>
                      </div>

                      <div>
                        <h6 className="font-medium mb-2">Template Guide</h6>
                        <p className="mb-2">All columns are required except for 'conferred_grad_date'. These are the questions to which the columns in your CSV template correspond:</p>
                        
                        <div className="bg-gray-50 p-3 rounded text-xs space-y-2 max-h-64 overflow-y-auto">
                          <div><strong>1) Student Id (student_id)</strong></div>
                          <div><strong>2) First Name (first_name)</strong></div>
                          <div><strong>3) Last Name (last_name)</strong></div>
                          <div><strong>4) NACME Block Grant Scholarship Recipient? (block_grant_scholar)</strong><br/>
                            Acceptable answers: Y/N</div>
                          <div><strong>5) NACME Corporate Scholarship Recipient? (corporate_scholar)</strong><br/>
                            Acceptable answers: Y/N</div>
                          <div><strong>6) NACME Special Scholarship Recipient? (special_scholarship)</strong><br/>
                            Acceptable answers: Y/N</div>
                          <div><strong>7) National Virtual Bridge Scholarship Recipient (nvb_scholar)</strong><br/>
                            Acceptable answers: Y/N</div>
                          <div><strong>8) Student's university email address: (university_email)</strong></div>
                          <div><strong>9) Scholar Status: (applicant_status)</strong><br/>
                            Acceptable answers: Graduated, Continuing, Health Leave of Absence, Co-Op Leave of Absence, Temporary Leave Status, Disqualification Withdrawal, University Withdrawal, External Transfer, Permanent Leave</div>
                          <div><strong>10) Date/Term of Initial NACME Block Scholarship: (date_of_initial_block_term)</strong><br/>
                            You must format the column of your CSV to display the date as follows: YYYY-MM-DD. In Excel, right click on date columns, then select 'Format Cells'. Select 'Custom' from the 'Category' window and enter yyyy-mm-dd in the 'Type' field.</div>
                          <div><strong>11) Conferred Graduation Date (if student has graduated): (conferred_grad_date)</strong><br/>
                            Follow instructions for entering dates in #10 above.</div>
                          <div><strong>12) Current Cumulative GPA: (gpa)</strong></div>
                          <div><strong>13) Expected graduation date: (expected_grad_date)</strong><br/>
                            Follow instructions for entering dates in #10 above.</div>
                          <div><strong>14) Entering Major at Matriculation: (major_at_matriculation)</strong><br/>
                            Enter with the following capitalization: Biomedical Engineering, Mechanical Engineering. Do not enter all caps.</div>
                          <div><strong>15) Current Major or Major of Completed Degree: (major)</strong><br/>
                            Follow capitalization instructions in #14.</div>
                          <div><strong>16) Classification: (current_academic_class)</strong><br/>
                            Enter as follows, observing spacing and punctuation, including hyphens: College - 1st Year, College - 2nd Year, College - 3rd Year, College - 4th Year, College - 5th Year, NA are the acceptable responses.</div>
                          <div><strong>17) Citizenship: (citizenship_status)</strong><br/>
                            Enter as follows, observing capitalization and punctuation (i.e., parentheses): US CITIZEN, NON US CITIZEN (PERMANENT RESIDENT), NON US CITIZEN (PERMIT), NON US CITIZEN (OTHER) are the acceptable responses.</div>
                          <div><strong>18) Gender: (gender)</strong><br/>
                            Enter as follows, observing capitalization format: Male, Female, Nonconforming, Transgender Male, Transgender Female, Non-Binary, NonDisclose are the acceptable responses.</div>
                          {raceRequired && (
                            <div><strong>19) Race: (race)</strong><br/>
                              Enter as follows, observing capitalization, punctuation, and spacing format: AA/BLACK, AL/ALASKANAT, ASIAN, LATINx/o/a, NonDisclose, PI/NatHawaii, WHITE, MULTI-RACIAL are the acceptable responses.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add New Students Section - Only for Primary Verifiers */}
        {dashboardData?.partner?.is_primary_verifier && (
          <div id="add-students-section" className="bg-white shadow rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Add New Students (Create records for new students, e.g., incoming freshmen)</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Upload new student data for admin approval
                </p>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Primary Verifier
              </span>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowAddNewStudents(!showAddNewStudents)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {showAddNewStudents ? (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                    </svg>
                    Hide Add New Students
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Show Add New Students
                  </>
                )}
              </button>
            </div>

            {showAddNewStudents && (
              <div className="mt-6 space-y-4">
                {/* Step 1: Download Template */}
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Step 1: Download Template</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Download a CSV template for adding new students to your university
                  </p>
                  <button
                    onClick={downloadNewStudentTemplate}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download New Students Template
                  </button>
                </div>

                {/* Step 2: Upload CSV */}
                <div className="border-l-4 border-blue-400 pl-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Step 2: Upload New Students CSV</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Fill out the template with new student information and upload for admin approval
                  </p>

                  {/* Enhanced Validation Rules Display */}
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">📋 CSV Format Guidelines & Validation Rules:</h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      <p><strong>Required Fields:</strong> first_name, last_name, university_email</p>
                      <p><strong>Student ID:</strong> Leave BLANK for new students (system will generate)</p>
                      <p><strong>Date Fields (YYYY-MM-DD format):</strong> matriculation_date, expected_grad_date, conferred_grad_date, date_of_initial_block_term</p>
                      <p><strong>GPA:</strong> Decimal 0.00-4.00 or leave blank</p>
                      <p><strong>Scholarship Fields (Y/N only):</strong> block_grant_scholar, corporate_scholar, special_scholarship, nvb_scholar</p>
                      <p><strong>Classification:</strong> College - 1st Year, College - 2nd Year, College - 3rd Year, College - 4th Year, College - 5th Year, NA</p>
                      <p><strong>Citizenship:</strong> US CITIZEN, NON US CITIZEN (PERMANENT RESIDENT), NON US CITIZEN (PERMIT), NON US CITIZEN (OTHER)</p>
                      <p><strong>Gender:</strong> Male, Female, Nonconforming, Transgender Male, Transgender Female, Non-Binary, NonDisclose</p>
                      {raceRequired && (
                        <p><strong>Race:</strong> AA/BLACK, AL/ALASKANAT, ASIAN, LATINx/o/a, NonDisclose, PI/NatHawaii, WHITE, MULTI-RACIAL</p>
                      )}
                      <p><strong>Status:</strong> Graduated, Continuing, Health Leave of Absence, Co-Op Leave of Absence, Temporary Leave Status, Disqualification Withdrawal, University Withdrawal, External Transfer, Permanent Leave</p>
                      <p><strong>Major Fields:</strong> Use proper capitalization (e.g., "Biomedical Engineering")</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 mt-4">
                    <div className="flex-1">
                      <input
                        id="new-student-csv-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleNewStudentFileSelect}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                      />
                    </div>
                    <button
                      onClick={uploadNewStudentsCSV}
                      disabled={!newStudentFile || newStudentUploadLoading}
                      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                        !newStudentFile || newStudentUploadLoading
                          ? 'bg-gray-300 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      }`}
                    >
                      {newStudentUploadLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Upload CSV
                        </>
                      )}
                    </button>
                  </div>

                  {newStudentFile && (
                    <div className="mt-2 text-sm text-gray-600">
                      Selected: {newStudentFile.name} ({(newStudentFile.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>

                {/* Requirements */}
                <div className="bg-gray-50 rounded-md p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">CSV Requirements for New Students:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Use the downloaded template for correct formatting</li>
                    <li>• Leave student_id field empty for new students</li>
                    <li>• All fields are required except "conferred_grad_date"</li>
                    <li>• Dates must be in YYYY-MM-DD format</li>
                    <li>• GPA should be in 0.00 format (0.0 to 4.0)</li>
                    <li>• Use exact values for dropdown fields (Y/N, status options, etc.)</li>
                    <li>• Each student requires a unique university email address</li>
                  </ul>
                  
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <div className="flex">
                      <svg className="h-5 w-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="text-sm text-yellow-800">
                        <strong>Important:</strong> New students will be sent to admin for approval before being added to the main database. You'll receive a confirmation once the upload is processed.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Upload Results Modal */}
        {showUploadResults && csvUploadResults && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    CSV Upload Results
                  </h3>
                  <button
                    onClick={closeUploadResults}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {csvUploadResults.summary.total_rows}
                    </div>
                    <div className="text-sm text-gray-600">Total Rows</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {csvUploadResults.summary.successful_updates}
                    </div>
                    <div className="text-sm text-gray-600">Successful</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {csvUploadResults.summary.failed_updates}
                    </div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>

                {/* Success/Failure Details */}
                <div className="space-y-4">
                  {csvUploadResults.successful.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-green-600 mb-2">
                        ✅ Successfully Updated ({csvUploadResults.successful.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto bg-green-50 p-3 rounded">
                        {csvUploadResults.successful.map((success, index) => (
                          <div key={index} className="text-sm text-gray-700">
                            Row {success.row}: {success.student_name} (ID: {success.student_id})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {csvUploadResults.failed.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-red-600 mb-2">
                        ❌ Failed Updates ({csvUploadResults.failed.length})
                      </h4>
                      <div className="max-h-40 overflow-y-auto bg-red-50 p-3 rounded">
                        {csvUploadResults.failed.map((failure, index) => (
                          <div key={index} className="text-sm mb-2 pb-2 border-b border-red-200 last:border-b-0">
                            <div className="font-medium">Row {failure.row} - Student ID: {failure.student_id}</div>
                            <div className="text-red-600 ml-2">
                              {failure.errors.map((error, errorIndex) => (
                                <div key={errorIndex}>• {error}</div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <div className="mt-6 text-center">
                  <button
                    onClick={closeUploadResults}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New Student Upload Results Modal */}
        {showNewStudentResults && newStudentUploadResults && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    New Student Upload Results
                  </h3>
                  <button
                    onClick={() => {
                      setShowNewStudentResults(false);
                      setNewStudentUploadResults(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {newStudentUploadResults.summary.total_rows}
                    </div>
                    <div className="text-sm text-gray-600">Total Rows</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {newStudentUploadResults.summary.successful_submissions || 0}
                    </div>
                    <div className="text-sm text-gray-600">Successful Submissions</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {newStudentUploadResults.summary.failed_submissions || 0}
                    </div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>

                {/* Failed Uploads */}
                {newStudentUploadResults.failed && newStudentUploadResults.failed.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-red-700 mb-3">
                      Failed Uploads ({newStudentUploadResults.failed.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto border border-red-200 rounded-md">
                      <table className="min-w-full divide-y divide-red-200">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Row</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Student Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Email</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase">Errors</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-red-200">
                          {newStudentUploadResults.failed.map((item, index) => (
                            <tr key={index} className="hover:bg-red-50">
                              <td className="px-4 py-2 text-sm text-gray-900">{item.row}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.student_name || 'N/A'}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.university_email || 'N/A'}</td>
                              <td className="px-4 py-2 text-sm text-red-600">
                                <ul className="list-disc list-inside">
                                  {item.errors.map((error, errIndex) => (
                                    <li key={errIndex}>{error}</li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Successful Uploads */}
                {newStudentUploadResults.successful && newStudentUploadResults.successful.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-green-700 mb-3">
                      Successfully Submitted ({newStudentUploadResults.successful.length})
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <p className="text-sm text-green-800 mb-2">
                        ✅ The following students have been submitted for admin approval:
                      </p>
                      <div className="max-h-40 overflow-y-auto">
                        <ul className="list-disc list-inside text-sm text-green-700">
                          {newStudentUploadResults.successful.map((item, index) => (
                            <li key={index}>
                              {item.student_name} ({item.university_email})
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* All Failed Message */}
                {newStudentUploadResults.failed && 
                 newStudentUploadResults.failed.length > 0 && 
                 (!newStudentUploadResults.successful || newStudentUploadResults.successful.length === 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-800">
                      ❌ No students were uploaded due to validation errors. Please fix the errors above and try again.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div id="recent-activity-section" className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="text-sm text-gray-600">
            <p>
              You have verified{' '}
              <span className="font-semibold text-gray-900">
                {dashboardData?.statistics.recent_verifications_30d || 0}
              </span>{' '}
              students in the last 30 days.
            </p>
            {dashboardData?.partner.last_login && (
              <p className="mt-2">
                Last login: {new Date(dashboardData.partner.last_login).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {/* Priority Students Section - Quick Verify */}
        {dashboardData?.priority_students && dashboardData.priority_students.length > 0 && (
          <div id="priority-students-section" className="mt-8 bg-gradient-to-r from-orange-50 to-red-50 shadow-lg rounded-lg border-l-4 border-orange-500">
            <div className="px-6 py-4 border-b border-orange-200 bg-white bg-opacity-80">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h2 className="text-lg font-semibold text-gray-900">Priority Students - Quick Verify</h2>
                  <p className="mt-1 text-sm text-gray-600">Students who have applied for internships and need immediate verification</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData.priority_students.map(student => (
                    <tr key={student.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{student.university_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.applied_for_this_cycle === 'Yes' && student.partner_has_verified_status !== 'verified' && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            🔴 NEW APPLICANT
                          </span>
                        )}
                        {student.quick_verified && student.partner_has_verified_status !== 'verified' && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            ⚠️ PARTIAL
                          </span>
                        )}
                        {student.partner_has_verified_status === 'verified' && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            ✓ VERIFIED
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {student.partner_has_verified_status !== 'verified' && (
                          <button
                            onClick={() => openQuickVerify(student)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Quick Verify
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Quick Verify Modal */}
      {showQuickVerify && selectedStudent && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeQuickVerify}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              {quickVerifySuccess ? (
                <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Quick Verification Successful!
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {selectedStudent.name} has been quick verified. Remember to complete full verification later.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleQuickVerifySubmit}>
                  <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Quick Verify: {selectedStudent.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Complete these 4 essential fields. Full verification required later.
                    </p>

                    <div className="space-y-4">
                      {/* Scholar Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          1. Scholar Status (Fall 2025) <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={quickVerifyData.scholar_status}
                          onChange={(e) => setQuickVerifyData({...quickVerifyData, scholar_status: e.target.value})}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select...</option>
                          {scholarStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>

                      {/* Current GPA */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          2. Current Cumulative GPA (Spring 2025) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={quickVerifyData.gpa}
                          onChange={(e) => setQuickVerifyData({...quickVerifyData, gpa: e.target.value})}
                          placeholder="e.g., 3.75"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Current Major */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          3. Current Major or Major of Completed Degree <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={quickVerifyData.major}
                          onChange={(e) => setQuickVerifyData({...quickVerifyData, major: e.target.value})}
                          placeholder="e.g., Computer Science"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Classification */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          4. Classification (Fall 2025) <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={quickVerifyData.classification}
                          onChange={(e) => setQuickVerifyData({...quickVerifyData, classification: e.target.value})}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select...</option>
                          {classificationOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {error && (
                      <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={quickVerifyLoading}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {quickVerifyLoading ? 'Verifying...' : 'Quick Verify'}
                    </button>
                    <button
                      type="button"
                      onClick={closeQuickVerify}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Priority Alert CTA Modal */}
      {showPriorityAlert && (
        <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Internship Applicants Need Verification
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please approve these internship applicants ASAP, so recruiters may review their resumes!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={scrollToPriorityStudents}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Quick Verify Now
                </button>
                <button
                  type="button"
                  onClick={() => setShowPriorityAlert(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;